import React from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { DbProduct } from '../../utils/api';
import { useFavorites } from '../auth/FavoritesContext';
import { useAuth } from '../auth/AuthContext';

type ProductCardProps = DbProduct;

const ProductCard = ({
  id,
  user_id,
  title,
  price,
  image_url,
  club,
  category,
  size,
  description,
  seller_name,
  seller_club,
  created_at,
}: ProductCardProps) => {
  const { isAuthenticated } = useAuth();
  const { isFavorited, toggle } = useFavorites();
  const favorited = isFavorited(id);

  const isNew = (Date.now() - new Date(created_at).getTime()) < 24 * 60 * 60 * 1000;

  const formattedDate = new Date(created_at).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <Link to={`/products/${id}`} className="block">
      <div className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden">
        {/* Bild-Container */}
        <div className="relative overflow-hidden aspect-square">
          <img
            src={image_url ? `${window.location.origin}${image_url}` : '/placeholder-image.jpg'}
            alt={title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
          />

          {/* NEU-Badge */}
          {isNew && (
            <div className="absolute top-2 left-2 z-20">
              <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-lg animate-pulse">
                NEU
              </span>
            </div>
          )}

          {/* Kategorie-Badge */}
          <div className="absolute top-2 right-2 z-10">
            <Link
              to={`/kategorie/${encodeURIComponent(category)}`}
              onClick={e => e.stopPropagation()}
              className="block bg-gray-900 hover:bg-gray-700 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm transition-colors"
            >
              {category}
            </Link>
          </div>

          {/* Größen-Badge — nur wenn kein NEU-Badge */}
          {size && !isNew && (
            <div className="absolute top-2 left-2 z-10">
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-1 rounded-full text-xs sm:text-sm shadow-lg">
                {size}
              </span>
            </div>
          )}

          {/* Gradient-Overlay beim Hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Schnellansicht-Button */}
          <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 z-10">
            <button
              onClick={(e) => e.preventDefault()}
              className="w-full bg-white/90 backdrop-blur-sm text-gray-800 py-2 rounded-lg font-semibold text-sm hover:bg-white transition-colors shadow-lg"
            >
              Schnellansicht
            </button>
          </div>
        </div>

        {/* Info-Bereich */}
        <div className="p-2 sm:p-4">
          <h3 className="font-semibold text-gray-800 text-sm sm:text-lg mb-1 truncate">
            {title}
          </h3>

          {/* Verkäufer Info */}
          <div className="mb-1 sm:mb-2">
            {(seller_club || club) && (seller_club || club) !== 'Vereinslos' ? (
              <Link
                to={`/club/${encodeURIComponent(seller_club || club)}`}
                onClick={e => e.stopPropagation()}
                className="text-orange-500 text-xs truncate hover:underline block"
              >
                {seller_club || club}
              </Link>
            ) : (
              <p className="text-gray-500 text-xs truncate">Vereinslos</p>
            )}
          </div>
          {seller_name && (
            <Link
              to={`/seller/${user_id}`}
              onClick={e => e.stopPropagation()}
              className="text-xs text-orange-500 hover:underline truncate block"
            >
              {seller_name}
            </Link>
          )}

          {/* Beschreibung - nur auf größeren Bildschirmen */}
          {description && (
            <p className="text-gray-700 mb-2 line-clamp-2 hidden sm:block text-sm">
              {description}
            </p>
          )}

          <div className="flex justify-between items-center mt-1 sm:mt-4">
            <p className="text-sm sm:text-2xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
              {Number(price).toFixed(2)} €
            </p>
            <div className="flex items-center gap-2">
              <p className="hidden sm:block text-xs sm:text-sm text-gray-500">
                {formattedDate}
              </p>
              {isAuthenticated && (
                <button
                  onClick={(e) => { e.preventDefault(); toggle(id); }}
                  className="p-1.5 rounded-full hover:bg-red-50 transition-colors"
                  title={favorited ? 'Aus Merkliste entfernen' : 'Zur Merkliste hinzufügen'}
                >
                  <Heart className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${favorited ? 'text-red-500 fill-red-500' : 'text-gray-400 hover:text-red-500'}`} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
